import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Store, Truck, Loader2 } from "lucide-react";
import { api, apiError } from "../lib/api";
import { rupiah } from "../lib/format";
import { useCart } from "../context/CartContext";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const [kiosks, setKiosks] = useState([]);
  const [fulfillment, setFulfillment] = useState("kedai");
  const [kioskId, setKioskId] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [payment, setPayment] = useState("QRIS");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  useEffect(() => { api.get("/kiosks").then((r) => { setKiosks(r.data.kiosks); if (r.data.kiosks[0]) setKioskId(r.data.kiosks[0].id); }); }, []);

  const totalWeight = items.reduce((s, i) => s + i.qty * 0.2, 0); // rough client preview
  const handling = fulfillment === "kedai" ? 15000 : 0;

  const submit = async () => {
    if (fulfillment === "kedai" && !kioskId) return toast.error("Pilih kedai pengambilan");
    if (fulfillment === "delivery" && !address) return toast.error("Isi alamat pengiriman");
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ product_slug: i.slug, qty: i.qty, variant: i.variant })),
        fulfillment, kiosk_id: fulfillment === "kedai" ? kioskId : null,
        address, phone, payment_method: payment,
      });
      clear();
      toast.success("Pesanan berhasil dibuat!");
      nav(`/pesanan/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  if (items.length === 0) { nav("/keranjang"); return null; }

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <h1 className="font-display text-2xl font-bold text-stone-900 mb-6">Checkout</h1>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white rounded-2xl border border-stone-200 p-5">
            <h3 className="font-display font-semibold mb-3">Metode Pengambilan</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <button data-testid="ff-kedai" onClick={() => setFulfillment("kedai")} className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition ${fulfillment === "kedai" ? "border-emerald-600 bg-emerald-50" : "border-stone-200"}`}>
                <Store className="text-emerald-600 mt-0.5" size={20} />
                <div><p className="font-semibold text-sm">Ambil di Kedai</p><p className="text-xs text-stone-500">Dibantu agen lokal (+Rp15.000)</p></div>
              </button>
              <button data-testid="ff-delivery" onClick={() => setFulfillment("delivery")} className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition ${fulfillment === "delivery" ? "border-emerald-600 bg-emerald-50" : "border-stone-200"}`}>
                <Truck className="text-emerald-600 mt-0.5" size={20} />
                <div><p className="font-semibold text-sm">Kirim ke Alamat</p><p className="text-xs text-stone-500">Diantar dari gudang Indo</p></div>
              </button>
            </div>

            {fulfillment === "kedai" ? (
              <div className="mt-4">
                <label className="text-sm font-medium text-stone-600">Pilih Kedai Terdekat</label>
                <select data-testid="kiosk-select" value={kioskId} onChange={(e) => setKioskId(e.target.value)} className="mt-1.5 w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-emerald-400 bg-white">
                  {kiosks.map((k) => <option key={k.id} value={k.id}>{k.name} — {k.subdistrict}, {k.city}</option>)}
                </select>
              </div>
            ) : (
              <div className="mt-4">
                <label className="text-sm font-medium text-stone-600">Alamat Lengkap</label>
                <textarea data-testid="address-input" value={address} onChange={(e) => setAddress(e.target.value)} rows={3} className="mt-1.5 w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-emerald-400" placeholder="Jl. ... , Kelurahan, Kecamatan, Kota" />
              </div>
            )}
            <div className="mt-4">
              <label className="text-sm font-medium text-stone-600">No. HP / WhatsApp</label>
              <input data-testid="phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5 w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-emerald-400" placeholder="0812xxxxxxxx" />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-stone-200 p-5">
            <h3 className="font-display font-semibold mb-3">Metode Pembayaran</h3>
            <div className="grid grid-cols-3 gap-3">
              {["QRIS", "Transfer Bank", "Bayar di Kedai"].map((m) => (
                <button key={m} data-testid={`pay-${m}`} onClick={() => setPayment(m)} className={`p-3 rounded-xl border-2 text-sm font-medium transition ${payment === m ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-stone-200 text-stone-600"}`}>{m}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-stone-200 p-5 h-fit sticky top-20">
          <h3 className="font-display font-semibold mb-4">Ringkasan Pesanan</h3>
          <div className="space-y-2 max-h-40 overflow-auto no-scrollbar mb-3">
            {items.map((it, i) => (
              <div key={i} className="flex justify-between text-xs text-stone-600"><span className="truncate pr-2">{it.qty}× {it.title}</span><span>{rupiah(it.qty * it.price_idr)}</span></div>
            ))}
          </div>
          <div className="border-t border-stone-100 pt-3 space-y-2 text-sm">
            <div className="flex justify-between text-stone-600"><span>Subtotal barang</span><b>{rupiah(subtotal)}</b></div>
            <div className="flex justify-between text-stone-600"><span>Ongkir China→Indo</span><span className="text-stone-400 text-xs">dihitung server</span></div>
            {handling > 0 && <div className="flex justify-between text-stone-600"><span>Biaya kedai</span><b>{rupiah(handling)}</b></div>}
          </div>
          <button data-testid="place-order-btn" disabled={loading} onClick={submit} className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-xl transition flex items-center justify-center gap-2 disabled:opacity-60">
            {loading && <Loader2 className="animate-spin" size={18} />}Buat Pesanan
          </button>
        </div>
      </div>
    </div>
  );
}
